<script setup lang="ts">
/**
 * BassLyricPopover
 *
 * Inline-Popover zum Bearbeiten der **Bass-Notation** (Bass-Lyric `number="3"`/`name="Bass"`) der
 * ausgewählten Note(n)/Pause(n)/Bass-Träger der Griffschrift-**Ausgabe**. Schwebt als absolutes
 * Overlay über der Partitur, verankert **unterhalb** der primären Note
 * (`selectedNoteX/Y` aus dem `ScoreEditContext`), damit die Bass-Zeile sichtbar bleibt.
 *
 * Eingabefeld (freier Text, z. B. `Wa` oder `G7`) + Schnellwahl-Chips (Großbuchstabe = Bass-Knopf,
 * Kleinbuchstabe = Akkord-Knopf; Umschalt+Klick hängt an). Enter/„Übernehmen" emittiert `apply`,
 * „Löschen" (oder leerer Text) emittiert `remove`, Esc/„×" schließt – Esc in der **Capture-Phase**
 * (`useCaptureEscape`), damit die Notenauswahl der `ScoreEditToolbar` erhalten bleibt.
 * `@keydown.stop` auf der Box: Leertaste/Pfeile/Entf dürfen die window-`onKeyStroke`-Handler
 * (Wiedergabe, Navigation, Löschen) nicht erreichen. Das Anwenden (Store-Write) übernimmt der
 * Parent (`MusicXmlPreview` → `applyBassLyric`).
 */
import {computed, nextTick, ref, watch} from 'vue';
import {useElementSize} from '@vueuse/core';
import {Icon} from '@iconify/vue';

import {storeToRefs} from 'pinia';

import {bassSyllables} from '@/components/harmonika/bassLayout';
import {useSettingsStore} from '@/stores/settings';
import {injectScoreEditContext} from './editContext';
import {clampedPosition, PANEL_MARGIN} from './panelPosition';
import {useCaptureEscape} from '@/composables/useCaptureEscape';
import {focusWithoutScroll} from '@/shared/focus';

const {currentText = null, noteCount = 1} = defineProps<{
  /** Aktueller Bass-Text der primären Note (Vorbelegung); null = keine Bass-Lyric. */
  currentText?: string | null;
  /** Anzahl der Ziel-Elemente (Mehrfachauswahl → alle bekommen denselben Text). */
  noteCount?: number;
}>();

const emit = defineEmits<{
  (e: 'apply', text: string): void;
  (e: 'remove'): void;
}>();

const isOpen = defineModel<boolean>('isOpen', {default: false, type: Boolean});

/**
 * Schnellwahl: die Griffschrift-Namen des **konfigurierten** Bass-Layouts (Großbuchstabe =
 * Grundbass, Kleinbuchstabe = Akkordknopf). Bewusst abgeleitet statt hartkodiert – wer seine
 * Bauart im Harmonika-Tab umbenennt oder Knöpfe entfernt, bekommt hier dieselben Namen
 * angeboten wie in der generierten Bass-Zeile.
 */
const {bassLayout} = storeToRefs(useSettingsStore());
const bassChips = computed<string[]>((oldValue) => {
  const next = bassSyllables(bassLayout.value);
  if (oldValue && oldValue.length === next.length && oldValue.every((v, i) => v === next[i])) {
    return oldValue;
  }
  return next;
});
/** Abstand unter der Note (px), damit Notenkopf und Bass-Silbe frei bleiben. */
const BELOW_NOTE_OFFSET = 56;

const {selectedNoteX, selectedNoteY} = injectScoreEditContext();

// ============ Position (unterhalb der Note) ============

const panelRef = ref<HTMLElement | null>(null);
const {width: panelWidth, height: panelHeight} = useElementSize(panelRef);

const panelTop = computed(() => {
  const h = panelHeight.value || 0;
  const extent = panelRef.value?.parentElement?.clientHeight ?? 0;
  const anchor = selectedNoteY.value === null ? null : selectedNoteY.value + BELOW_NOTE_OFFSET + h / 2;
  return clampedPosition(anchor, h, extent, PANEL_MARGIN);
});

const panelLeft = computed(() => {
  const w = panelWidth.value || 0;
  const extent = panelRef.value?.parentElement?.clientWidth ?? 0;
  return clampedPosition(selectedNoteX.value, w, extent, Math.max(PANEL_MARGIN, extent - w - PANEL_MARGIN));
});

// ============ Eingabe ============

const draft = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
const countLabel = computed(() => (noteCount === 1 ? '1 Element' : `${noteCount} Elemente`));

// Beim Öffnen mit dem aktuellen Text vorbelegen und den Fokus ins Feld setzen.
watch(isOpen, async (open) => {
  if (!open) return;
  draft.value = currentText ?? '';
  await nextTick();
  focusWithoutScroll(inputRef.value);
  inputRef.value?.select();
}, {immediate: true});

// Auswahl springt bei offenem Popover auf eine andere Note → Vorbelegung nachziehen.
watch(() => currentText, (text) => {
  if (isOpen.value) draft.value = text ?? '';
});

function apply(): void {
  const text = draft.value.trim();
  if (!text) emit('remove');
  else emit('apply', text);
}

function remove(): void {
  emit('remove');
}

function close(): void {
  isOpen.value = false;
}

function pickChip(chip: string, append: boolean): void {
  draft.value = append ? `${draft.value}${chip}` : chip;
  focusWithoutScroll(inputRef.value);
}

useCaptureEscape(isOpen, close);
</script>

<template>
  <!-- Volle Fläche über der Partitur, transparent/pointer-events-none; nur die Box ist interaktiv. -->
  <div v-show="isOpen" class="pointer-events-none absolute inset-0 z-30">
    <div
        ref="panelRef"
        role="dialog"
        aria-label="Bass-Notation bearbeiten"
        class="pointer-events-auto absolute w-64 rounded-xl border border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm animate-fade animate-duration-150"
        :style="{ top: `${panelTop}px`, left: `${panelLeft}px` }"
        @keydown.stop
    >
      <div class="mb-2 flex items-center gap-2">
        <Icon icon="game-icons:f-clef" class="text-indigo-600"/>
        <span class="text-sm font-semibold text-gray-800">Bass-Notation</span>
        <span class="ml-auto text-xs text-gray-500">{{ countLabel }}</span>
        <button
            type="button"
            class="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            title="Schließen (Esc)"
            aria-label="Schließen"
            @click="close"
        >
          <Icon icon="fa6-solid:xmark"/>
        </button>
      </div>

      <input
          ref="inputRef"
          v-model="draft"
          type="text"
          maxlength="8"
          class="w-full rounded-lg border border-gray-300 px-2 py-1.5 font-mono text-base text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
          placeholder="z. B. A, a, Wa, G7"
          aria-label="Bass-Text"
          @keydown.enter.prevent="apply"
      >

      <div
          class="mt-2 flex flex-wrap gap-1"
          title="Großbuchstabe = Bass-Knopf, Kleinbuchstabe = Akkord-Knopf · Umschalt+Klick hängt an"
      >
        <button
            v-for="chip in bassChips"
            :key="chip"
            type="button"
            class="h-7 min-w-7 rounded-md border border-gray-200 bg-gray-50 px-1.5 font-mono text-sm text-gray-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50"
            :aria-label="`Bass ${chip}`"
            @click="pickChip(chip, $event.shiftKey)"
        >
          {{ chip }}
        </button>
      </div>

      <div class="mt-3 flex items-center justify-between gap-2">
        <button
            type="button"
            class="btn btn-ghost flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-700"
            title="Bass-Notation entfernen"
            @click="remove"
        >
          <Icon icon="fa6-solid:trash"/>
          Löschen
        </button>
        <button
            type="button"
            class="btn btn-primary flex items-center gap-1 px-3 py-1 text-xs"
            title="Übernehmen (Enter)"
            @click="apply"
        >
          <Icon icon="fa6-solid:check"/>
          Übernehmen
        </button>
      </div>
    </div>
  </div>
</template>
